/**
 * keyCombo — parse / match / format keyboard combos for the ONE shared hotkey
 * layer (CLAUDE.md standing rule 14, §18.6, ADR-0013).
 *
 * A combo string is `"mod+s"`, `"alt+n"`, `"f2"`, `"esc"`, `"alt+left"`, a
 * shifted-punctuation key like `"?"`, or a space-separated chord like `"g h"`.
 * `mod` maps to Ctrl on Windows/Linux and Cmd (meta) on macOS, so one binding is
 * correct on every platform.  Nothing here has React or DOM-registry knowledge —
 * it is pure parsing/matching so it can be unit-tested in isolation.
 */

/** A single parsed key combination (one step of a possibly-multi-step chord). */
export interface Combo {
  /** Normalized event-key (lowercase): 's', 'f2', 'escape', 'enter', 'arrowleft', '?'. */
  key: string;
  /** Ctrl on Win/Linux, Cmd (meta) on macOS — resolved at match time. */
  mod: boolean;
  ctrl: boolean;
  meta: boolean;
  alt: boolean;
  shift: boolean;
  /** For a shifted-punctuation key (e.g. '?'), don't require the shift flag on match. */
  ignoreShift: boolean;
}

/** True when running on a Mac — so `mod` matches Cmd and formats as ⌘. */
export const IS_MAC =
  typeof navigator !== 'undefined' &&
  /mac|iphone|ipad|ipod/i.test(navigator.platform || navigator.userAgent || '');

const MODIFIERS = new Set([
  'mod', 'ctrl', 'control', 'alt', 'opt', 'option',
  'shift', 'meta', 'cmd', 'command', 'win', 'windows', 'super',
]);

/** Map friendly key aliases to the normalized (lowercased KeyboardEvent.key) form. */
function normalizeKeyToken(t: string): string {
  switch (t) {
    case 'esc': case 'escape': return 'escape';
    case 'enter': case 'return': return 'enter';
    case 'del': case 'delete': return 'delete';
    case 'space': case 'spacebar': return 'space';
    case 'left': case 'arrowleft': return 'arrowleft';
    case 'right': case 'arrowright': return 'arrowright';
    case 'up': case 'arrowup': return 'arrowup';
    case 'down': case 'arrowdown': return 'arrowdown';
    default: return t;
  }
}

/** Parse one combo token (no spaces), e.g. `"mod+shift+s"`. */
export function parseCombo(input: string): Combo {
  const c: Combo = { key: '', mod: false, ctrl: false, meta: false, alt: false, shift: false, ignoreShift: false };
  const parts = input.trim().toLowerCase().split('+').map((s) => s.trim()).filter(Boolean);
  for (const p of parts) {
    if (MODIFIERS.has(p)) {
      if (p === 'mod') c.mod = true;
      else if (p === 'ctrl' || p === 'control') c.ctrl = true;
      else if (p === 'alt' || p === 'opt' || p === 'option') c.alt = true;
      else if (p === 'shift') c.shift = true;
      else c.meta = true; // meta / cmd / command / win / windows / super
    } else {
      c.key = normalizeKeyToken(p);
    }
  }
  c.ignoreShift = c.key === '?';
  return c;
}

/** Parse a combo or a space-separated chord (`"g h"`) into its ordered steps. */
export function parseSequence(input: string): Combo[] {
  return input.trim().split(/\s+/).filter(Boolean).map(parseCombo);
}

/** The normalized key for an event (lowercased KeyboardEvent.key). */
export function normalizeEventKey(e: KeyboardEvent): string {
  return (e.key || '').toLowerCase();
}

/**
 * Exact-modifier match: `Ctrl+S` never matches `Ctrl+Shift+S`.  `mod` resolves
 * to Ctrl (non-mac) or Cmd (mac).  A shift-insensitive key (`?`) ignores shift.
 */
export function comboMatchesEvent(c: Combo, e: KeyboardEvent, isMac: boolean = IS_MAC): boolean {
  if (normalizeEventKey(e) !== c.key) return false;
  const wantCtrl = c.mod ? !isMac : c.ctrl;
  const wantMeta = c.mod ? isMac : c.meta;
  if (e.ctrlKey !== wantCtrl) return false;
  if (e.metaKey !== wantMeta) return false;
  if (e.altKey !== c.alt) return false;
  if (!c.ignoreShift && e.shiftKey !== c.shift) return false;
  return true;
}

/**
 * The default for whether a combo may fire while an editable element is focused.
 * Modifier combos (so Ctrl+S works mid-typing), Esc and the F-keys are allowed;
 * a plain single key is not.  A binding can override with `allowInInput`.
 */
export function comboAllowsInput(c: Combo): boolean {
  if (c.mod || c.ctrl || c.meta || c.alt) return true;
  if (c.key === 'escape') return true;
  if (/^f\d{1,2}$/.test(c.key)) return true;
  return false;
}

/**
 * True when the event target is a place the user is typing — a text input,
 * textarea, select, contenteditable, or anything under `[data-hotkeys-ignore]`.
 * Non-typing inputs (button/checkbox/radio/…) are NOT editable targets.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== 'function') return false;
  if (el.closest('[data-hotkeys-ignore]')) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type;
    const nonTyping = new Set(['button', 'submit', 'reset', 'checkbox', 'radio', 'range', 'color', 'file', 'image']);
    return !nonTyping.has(type);
  }
  return el.isContentEditable === true;
}

const KEY_LABEL: Record<string, string> = {
  escape: 'Esc', arrowleft: '←', arrowright: '→', arrowup: '↑', arrowdown: '↓',
  space: 'Space', tab: 'Tab', delete: 'Del', pageup: 'PgUp', pagedown: 'PgDn',
};

function formatKey(key: string, isMac: boolean): string {
  if (key === 'enter') return isMac ? '↵' : 'Enter';
  const label = KEY_LABEL[key];
  if (label) return label;
  if (/^f\d{1,2}$/.test(key)) return key.toUpperCase();
  if (key.length === 1) return key === '?' ? '?' : key.toUpperCase();
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function formatOne(c: Combo, isMac: boolean): string {
  const parts: string[] = [];
  if (c.mod) parts.push(isMac ? '⌘' : 'Ctrl');
  if (c.ctrl) parts.push(isMac ? '⌃' : 'Ctrl');
  if (c.alt) parts.push(isMac ? '⌥' : 'Alt');
  if (c.shift) parts.push(isMac ? '⇧' : 'Shift');
  parts.push(formatKey(c.key, isMac));
  return parts.join('+');
}

/** Human-readable combo for a tooltip / cheat sheet, e.g. `"Ctrl+S"` / `"⌘+S"`. */
export function formatCombo(input: string, isMac: boolean = IS_MAC): string {
  return parseSequence(input).map((c) => formatOne(c, isMac)).join(' ');
}

/**
 * Serialize a live keydown into a canonical combo string (`"alt+shift+n"`) for the
 * key-capture editor (Phase 3 part 2). `mod` is used for the platform primary
 * (Cmd on macOS, Ctrl elsewhere), so the stored override matches on every OS.
 * Returns null for a lone modifier keydown (keep listening for the real key).
 */
export function comboFromEvent(e: KeyboardEvent, isMac: boolean = IS_MAC): string | null {
  const k = (e.key || '').toLowerCase();
  if (k === 'shift' || k === 'control' || k === 'alt' || k === 'meta') return null;
  const parts: string[] = [];
  if (isMac ? e.metaKey : e.ctrlKey) parts.push('mod');
  if (isMac && e.ctrlKey) parts.push('ctrl'); // a real Ctrl on macOS (rare)
  if (e.altKey) parts.push('alt');
  if (e.shiftKey) parts.push('shift');
  parts.push(normalizeKeyToken(k === ' ' ? 'space' : k));
  return parts.join('+');
}
